/**
 * useChains Hook
 * Fetches and manages EVM chain data from the Bridge API
 * Falls back to static chain configuration when API is not configured
 */

import { useQuery } from '@tanstack/react-query';

import { fetchChains, getEnabledChains, findChainById, findChainByName, isApiConfigured } from 'services';
import { VIEM_CHAIN_MAP, getChainDisplayInfo, getLockerAddress, isStaticChainEnabled } from 'config';
import type { Chain } from 'types';

/**
 * Static fallback chains when API is not configured
 * These are derived from the viem chain map
 */
const getStaticChains = (): Chain[] => {
  const chainIds = Object.keys(VIEM_CHAIN_MAP).map(Number);

  // Canonical API slugs (chains.short_name in the API DB). These are the keys
  // used for token-address lookups, so they must match the API even though the
  // display name (shortName) may differ — e.g. 11155111 displays as
  // "Ethereum Sepolia" but its address key is "sepolia".
  const apiSlugs: Record<number, string> = {
    11155111: 'sepolia',
    84532: 'base-sepolia',
    80002: 'polygon-amoy',
    421614: 'arbitrum-sepolia',
    97: 'bsc-testnet',
    46630: 'robinhood-testnet',
    1: 'ethereum',
    8453: 'base',
    137: 'polygon',
    42161: 'arbitrum',
    56: 'bsc',
    4663: 'robinhood',
  };

  // Per-chain block times in seconds. The finality estimate is
  // max(64, confirmations) × blockTime, so a wrong blockTime here quotes
  // Ethereum's ~15 min for 2s-block chains like Base. Values mirror the API
  // (chains.block_time); confirmations are nominal and get floored to 64 anyway.
  const blockTimes: Record<number, number> = {
    11155111: 12,
    84532: 2,
    80002: 2,
    421614: 0.25,
    97: 3,
    46630: 0.4,
    1: 12,
    8453: 2,
    137: 2,
    42161: 0.25,
    56: 3,
    4663: 0.1,
  };

  const confirmationFloors: Record<number, number> = {
    46630: 6000,
    4663: 15000,
  };

  return chainIds.map(chainId => {
    const displayInfo = getChainDisplayInfo(chainId);

    return {
      chainId,
      name: apiSlugs[chainId] ?? displayInfo.shortName.toLowerCase().replace(/\s+/g, '-'),
      shortName: displayInfo.shortName,
      explorerUrl: VIEM_CHAIN_MAP[chainId]?.blockExplorers?.default?.url || '',
      lockerAddress: getLockerAddress(chainId), // Get from config mapping
      confirmations: confirmationFloors[chainId] ?? 12,
      blockTime: blockTimes[chainId] ?? 12,
      isEnabled: isStaticChainEnabled(chainId),
      rpcUrl: VIEM_CHAIN_MAP[chainId]?.rpcUrls?.default?.http?.[0],
    };
  });
};

/**
 * Query key for chains data
 */
export const CHAINS_QUERY_KEY = ['bridge', 'chains'] as const;

/**
 * Hook to fetch all supported chains from the API
 * Falls back to static chains when API is not configured
 */
export function useChains() {
  const apiConfigured = isApiConfigured();

  const query = useQuery({
    queryKey: CHAINS_QUERY_KEY,
    queryFn: fetchChains,
    enabled: apiConfigured, // Only fetch if API is configured
    staleTime: 5 * 60 * 1000, // 5 minutes - chains don't change often
    gcTime: 30 * 60 * 1000, // 30 minutes cache
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000),
  });

  // Use static chains as fallback when API is not configured or returns empty
  const staticChains = getStaticChains();
  const chains = apiConfigured ? (query.data ?? []) : staticChains;
  const enabledChains = getEnabledChains(chains);

  // Helper functions
  const getChainById = (chainId: number): Chain | undefined => {
    return findChainById(chains, chainId);
  };

  const getChainByName = (name: string): Chain | undefined => {
    return findChainByName(chains, name);
  };

  const isChainSupported = (chainId: number): boolean => {
    const chain = findChainById(chains, chainId);

    return chain !== undefined && chain.isEnabled;
  };

  return {
    // Query state
    chains,
    enabledChains,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    isFetching: query.isFetching,

    // Helpers
    getChainById,
    getChainByName,
    isChainSupported,

    // Query actions
    refetch: query.refetch,
  };
}

/**
 * Hook to get a specific chain by ID with loading state
 */
export function useChainById(chainId: number | undefined) {
  const { chains, isLoading, isError, error } = useChains();

  const chain = chainId ? findChainById(chains, chainId) : undefined;

  return {
    chain,
    isLoading,
    isError,
    error,
    isSupported: chain !== undefined && chain.isEnabled,
  };
}

/**
 * Hook to get a specific chain by name
 */
export function useChainByName(chainName: string | undefined) {
  const { chains, isLoading, isError, error } = useChains();

  const chain = chainName ? findChainByName(chains, chainName) : undefined;

  return {
    chain,
    isLoading,
    isError,
    error,
    isSupported: chain !== undefined && chain.isEnabled,
  };
}
